import { Avatar, Button, Select } from '@bennows/boows-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useMe } from './useMe';
import { useSignOut } from './useSignOut';

/**
 * Account area pinned to the bottom of the sidebar (style guide 2.13): the
 * user's picture and name as the way to "Mein Profil" (S10-9), tenant
 * switcher (only with more than one membership) and sign-out. Switching the
 * tenant drops every cached query and reloads the current route in the new
 * tenant. The UI always addresses the user with "du" (human decision
 * 2026-10-01, the T130-10-9 form-of-address choice was removed).
 */
export function AccountArea() {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useSignOut();
  const switchTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const { response } = await api.PUT('/v1/me/active-tenant', { body: { tenant_id: tenantId } });
      if (!response.ok) throw new Error(`switch answered ${response.status}`);
    },
    onSuccess: () => {
      queryClient.clear();
      navigate(`${location.pathname}${location.search}`, { replace: true });
    },
  });

  if (me.isPending) {
    return (
      <div className="ui-account" role="status" aria-busy="true" aria-label={t('account.loading')}>
        <span className="ui-skeleton ui-skeleton--text" />
      </div>
    );
  }
  if (me.isError) {
    // Not a 401 (the gate handles that): keep a way to retry and to leave.
    return (
      <div className="ui-account">
        <p className="ui-account__error" role="alert">
          {t('account.loadFailed')}
        </p>
        <Button variant="outline" size="sm" onClick={() => void me.refetch()}>
          {t('error.retry')}
        </Button>
        <Button variant="outline" size="sm" loading={signOut.isPending} onClick={() => signOut.mutate()}>
          {t('account.signOut')}
        </Button>
      </div>
    );
  }
  if (!me.data?.active_tenant) return null;
  const { user, memberships, active_tenant: active } = me.data;

  return (
    <div className="ui-account">
      <NavLink to="/profile" className="ui-account__user" aria-label={t('account.profile', { name: user.display_name })}>
        <Avatar name={user.display_name} imageUrl={user.avatar ?? undefined} size="md" />
        <span className="ui-account__who">
          <span className="ui-account__name">{user.display_name}</span>
          {memberships.length > 1 ? null : <span className="ui-account__tenant">{active.name}</span>}
        </span>
      </NavLink>
      {memberships.length > 1 ? (
        <Select
          label={t('account.tenant')}
          options={memberships.map((membership) => ({ id: membership.tenant_id, label: membership.tenant_name }))}
          value={active.id}
          onChange={(id) => id !== active.id && switchTenant.mutate(id)}
          disabled={switchTenant.isPending}
          invalid={switchTenant.isError}
          help={switchTenant.isError ? t('account.switchFailed') : undefined}
        />
      ) : null}
      <Button variant="outline" size="sm" loading={signOut.isPending} onClick={() => signOut.mutate()}>
        {t('account.signOut')}
      </Button>
      {signOut.isError ? (
        <p className="ui-account__error" role="alert">
          {t('account.signOutFailed')}
        </p>
      ) : null}
    </div>
  );
}
